import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { JsonLdService, MetaService } from '@app/core';
import { CtaSectionComponent } from '@app/widgets';
import { map } from 'rxjs';
import { IOS_UDID_FINDER_FAQS } from '../../constants';
import { IosUdidFinderFaqSectionComponent } from '../ios-udid-finder-faq-section/ios-udid-finder-faq-section.component';
import { IosUdidFinderHeroSectionComponent } from '../ios-udid-finder-hero-section/ios-udid-finder-hero-section.component';
import { IosUdidFinderInfoSectionComponent } from '../ios-udid-finder-info-section/ios-udid-finder-info-section.component';
import { IosUdidFinderResultSectionComponent } from '../ios-udid-finder-result-section/ios-udid-finder-result-section.component';

@Component({
  selector: 'app-ios-udid-finder',
  imports: [
    IosUdidFinderHeroSectionComponent,
    IosUdidFinderResultSectionComponent,
    IosUdidFinderInfoSectionComponent,
    IosUdidFinderFaqSectionComponent,
    CtaSectionComponent,
  ],
  templateUrl: './ios-udid-finder.component.html',
  styleUrl: './ios-udid-finder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IosUdidFinderComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly queryParamMap = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map(parameters => ({
        udid: parameters.get('udid') || '',
        deviceName: parameters.get('deviceName') || '',
        imei: parameters.get('imei') || '',
        meid: parameters.get('meid') || '',
        serial: parameters.get('serial') || '',
        version: parameters.get('version') || '',
        product: parameters.get('product') || '',
      })),
    ),
  );

  protected readonly udid = computed(() => this.queryParamMap()?.udid || '');
  protected readonly deviceName = computed(
    () => this.queryParamMap()?.deviceName || '',
  );
  protected readonly imei = computed(() => this.queryParamMap()?.imei || '');
  protected readonly meid = computed(() => this.queryParamMap()?.meid || '');
  protected readonly serial = computed(
    () => this.queryParamMap()?.serial || '',
  );
  protected readonly version = computed(
    () => this.queryParamMap()?.version || '',
  );
  protected readonly product = computed(
    () => this.queryParamMap()?.product || '',
  );

  constructor(
    private readonly metaService: MetaService,
    private readonly jsonLdService: JsonLdService,
  ) {
    this.metaService.setTitle(
      'iOS UDID Finder — Find Your iPhone or iPad UDID',
    );
    this.metaService.setDescription(
      'Find your iPhone or iPad UDID instantly without a Mac or Xcode. Install a lightweight profile, get your UDID, device name, serial number, and iOS version in seconds.',
    );
    this.metaService.setKeywords([
      'UDID finder',
      'find UDID',
      'iPhone UDID',
      'iPad UDID',
      'iOS UDID',
      'get UDID without Mac',
      'UDID lookup',
      'device UDID',
      'find iPhone UDID online',
      'Capacitor device UDID',
    ]);
    this.jsonLdService.setStructuredData([
      JsonLdService.buildWebPageSchema(
        'iOS UDID Finder',
        'Find your iPhone or iPad UDID instantly without a Mac or Xcode.',
        'https://cloud.capawesome.io/tools/ios-udid-finder/',
      ),
      JsonLdService.buildFaqSchema(IOS_UDID_FINDER_FAQS),
      JsonLdService.buildHowToSchema(
        'How to Find Your iPhone or iPad UDID',
        'Find your iOS device UDID by installing a lightweight configuration profile.',
        [
          {
            name: 'Download the profile',
            text: 'Tap "Find My UDID" and allow the profile download.',
          },
          {
            name: 'Install the profile',
            text: 'Open Settings, tap "Profile Downloaded", and tap Install.',
          },
          {
            name: 'Get your device information',
            text: 'You will be redirected back with your UDID, device name, serial number, and more.',
          },
        ],
      ),
    ]);
  }
}
